import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert,
  Activity,
  Wifi,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  ClipboardCheck,
  BarChart3,
} from 'lucide-react';
import { getCaseSummary, formatCaseSummaryForDisplay } from '../../../services/caseSummary.service';
import { getMlRiskScore } from '../../../services/mlRiskScore.service';
import { getMovementSummary, getMovementStateConfig } from '../../../services/movementAnalytics.service';
import AddressDisplay from '../../AddressDisplay';
import { getGPSQualityStats, getSpoofHistory } from '../../../services/gpsPipeline.service';
import { getSmartGeofences, checkGeofenceCompliance } from '../../../services/smartGeofence.service';
import { getAnomalyHistory } from '../../../services/patternOfLife.service';
import { getClusterAssignment, getClusterLabelConfig } from '../../../services/patternClustering.service';

// Risk level colors
const getRiskColor = (score) => {
  if (score >= 4) return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Critical' };
  if (score >= 3) return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: 'High' };
  if (score >= 2) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', label: 'Medium' };
  return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Low' };
};

const getRiskLevelColor = (level) => {
  if (!level) return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', badge: 'bg-gray-100 text-gray-600' };
  const l = level.toLowerCase();
  if (l === 'critical' || l === 'very high') return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700' };
  if (l === 'high') return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700' };
  if (l === 'medium') return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' };
  return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' };
};

const getQualityColor = (quality) => {
  if (quality >= 80) return { text: 'text-green-600', label: 'Good' };
  if (quality >= 50) return { text: 'text-yellow-600', label: 'Fair' };
  return { text: 'text-red-600', label: 'Poor' };
};

const TrendIcon = ({ trend }) => {
  if (!trend) return <Minus size={16} className="text-gray-400" />;
  const t = trend.toLowerCase();
  if (t === 'increasing' || t === 'up' || t === 'worsening') return <TrendingUp size={16} className="text-red-500" />;
  if (t === 'decreasing' || t === 'down' || t === 'improving') return <TrendingDown size={16} className="text-green-500" />;
  return <Minus size={16} className="text-gray-400" />;
};

const MiniProgressBar = ({ value, max, color = 'bg-blue-500' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
      <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
  );
};

const PolOverviewTab = ({ personId, setActiveTab }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Section 1: Case summary (SQL)
  const [caseSummary, setCaseSummary] = useState(null);

  // Section 2: ML data
  const [riskScore, setRiskScore] = useState(null);
  const [movementSummary, setMovementSummary] = useState(null);
  const [gpsQuality, setGpsQuality] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [geofences, setGeofences] = useState(null);
  const [anomalyHistory, setAnomalyHistory] = useState(null);
  const [spoofHistory, setSpoofHistory] = useState(null);
  const [clusterAssignment, setClusterAssignment] = useState(null);

  const fetchOverviewData = useCallback(async () => {
    if (!personId) return;

    setLoading(true);
    setError(null);

    const pid = parseInt(personId, 10);

    // Fetch case summary AND ML data in parallel
    const results = await Promise.allSettled([
      getCaseSummary(pid),
      getMlRiskScore(pid),
      getMovementSummary(pid, 24),
      getGPSQualityStats(pid, 30),
      getSmartGeofences(pid),
      getAnomalyHistory(pid, 10),
      getSpoofHistory(pid, 7),
      getClusterAssignment(pid),
    ]);

    const [caseRes, riskRes, moveRes, gpsRes, geoRes, anomRes, spoofRes, clusterRes] = results;

    if (caseRes.status === 'fulfilled' && caseRes.value) {
      setCaseSummary(formatCaseSummaryForDisplay(caseRes.value));
    }
    if (riskRes.status === 'fulfilled') setRiskScore(riskRes.value);
    if (moveRes.status === 'fulfilled') setMovementSummary(moveRes.value);
    if (gpsRes.status === 'fulfilled') setGpsQuality(gpsRes.value);
    if (geoRes.status === 'fulfilled') setGeofences(geoRes.value);
    if (anomRes.status === 'fulfilled') setAnomalyHistory(anomRes.value);
    if (spoofRes.status === 'fulfilled') setSpoofHistory(spoofRes.value);
    if (clusterRes.status === 'fulfilled') setClusterAssignment(clusterRes.value);

    // If geofences loaded, try compliance check with last known location
    if (geoRes.status === 'fulfilled' && moveRes.status === 'fulfilled') {
      const lastLoc = moveRes.value?.lastKnownLocation || moveRes.value?.last_known_location;
      if (lastLoc?.latitude && lastLoc?.longitude) {
        try {
          const comp = await checkGeofenceCompliance(pid, lastLoc.latitude, lastLoc.longitude);
          setCompliance(comp);
        } catch {
          // Non-critical, ignore
        }
      }
    }

    // Only show error if both case summary AND all ML calls failed
    const mlResults = results.slice(1);
    const mlFailures = mlResults.filter(r => r.status === 'rejected');
    if (caseRes.status === 'rejected' && mlFailures.length === mlResults.length) {
      setError('Unable to load overview data. Please verify the Subject ID.');
    }

    setLoading(false);
  }, [personId]);

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  if (!personId) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <div className="text-6xl mb-4">📊</div>
        <h3 className="text-xl font-semibold mb-2">Subject Monitoring Overview</h3>
        <p className="text-gray-500">Enter a Subject ID above to view their monitoring dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#184A89]"></div>
        <span className="ml-4 text-gray-500">Loading overview...</span>
      </div>
    );
  }

  // ── Section 1 computed values (SQL case summary) ──
  const cs = caseSummary || {};
  const checkIns = cs.checkIns || {};
  const risk = cs.risk || {};
  const locationData = cs.locationData || {};
  const courtEvents = cs.courtEvents || {};
  const payments = cs.payments || {};

  const complianceStatus = cs.complianceStatus;
  const isComplianceGood = complianceStatus && complianceStatus.toLowerCase() !== 'non-compliant' && complianceStatus.toLowerCase() !== 'noncompliant';
  const riskLevelConfig = getRiskLevelColor(risk.currentRiskLevel);

  // ── Section 2 computed values (ML) ──
  const riskValue = riskScore?.riskScore ?? riskScore?.risk_score ?? null;
  const riskConfig = riskValue !== null ? getRiskColor(riskValue) : null;

  const moveState = movementSummary?.currentState || movementSummary?.current_state || movementSummary?.movement_state;
  const moveStateConfig = moveState ? getMovementStateConfig(moveState) : null;
  const lastLocation = movementSummary?.lastKnownLocation || movementSummary?.last_known_location;

  const avgQuality = gpsQuality?.averageQuality ?? gpsQuality?.average_quality;
  const qualityConfig = avgQuality != null ? getQualityColor(avgQuality) : null;

  const recentAnomalies = anomalyHistory?.detections || anomalyHistory?.items || [];
  const highAlerts = recentAnomalies.filter(a =>
    (a.alertLevel || a.alert_level) === 'high' || (a.alertLevel || a.alert_level) === 'medium'
  );

  const recentSpoofs = spoofHistory?.analyses || spoofHistory?.items || [];
  const flaggedSpoofs = recentSpoofs.filter(s =>
    (s.spoofProbability ?? s.spoof_probability ?? 0) > 0.3
  );

  const mlIsCompliant = compliance?.isCompliant ?? compliance?.is_compliant;

  const clusterLabel = clusterAssignment?.clusterLabel || clusterAssignment?.cluster_label;
  const clusterConfig = clusterLabel ? getClusterLabelConfig(clusterLabel) : null;

  const totalAlerts = highAlerts.length + flaggedSpoofs.length;

  const hasMlData = riskValue !== null || moveState || avgQuality != null ||
    mlIsCompliant !== undefined || clusterLabel || totalAlerts > 0;

  const mergedAlertCount = totalAlerts + (risk.riskAlertCount || 0);

  return (
    <div className="space-y-4">
      {/* Error */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* No data fallback */}
      {!caseSummary && !hasMlData && !loading && !error && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
          No data available yet for this subject. Run analysis on the Location or Risk tab to populate.
        </div>
      )}

      {/* ── Row 1: Status Strip ── */}
      {(caseSummary || hasMlData) && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="grid grid-cols-2 lg:grid-cols-4">

            {/* Compliance */}
            <div className="p-4 border-r border-b lg:border-b-0 border-gray-100">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                <ClipboardCheck size={13} />
                Compliance
              </div>
              <div className="flex items-center gap-2">
                {complianceStatus ? (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    isComplianceGood ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {complianceStatus}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">N/A</span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1.5">
                Check-in: <span className="font-semibold text-gray-700">{cs.checkInCompletionRateFormatted || '0%'}</span>
              </div>
            </div>

            {/* Risk (merged SQL + ML) */}
            <div className="p-4 border-b lg:border-b-0 lg:border-r border-gray-100">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                <ShieldAlert size={13} />
                Risk
              </div>
              {riskValue !== null ? (
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${riskConfig?.text || 'text-gray-800'}`}>
                    {riskValue.toFixed(2)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${riskConfig?.text} ${riskConfig?.bg}`}>
                    {riskConfig?.label}
                  </span>
                </div>
              ) : risk.currentRiskLevel ? (
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${riskLevelConfig.badge}`}>
                    {risk.currentRiskLevel}
                  </span>
                  <TrendIcon trend={risk.riskTrend} />
                </div>
              ) : (
                <span className="text-sm text-gray-400">Not assessed</span>
              )}
            </div>

            {/* Movement */}
            <div className="p-4 border-r border-gray-100">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                <Activity size={13} />
                Movement
              </div>
              {moveState ? (
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg">{moveStateConfig?.icon || '?'}</span>
                    <span className="text-sm font-semibold" style={{ color: moveStateConfig?.color }}>
                      {moveStateConfig?.label || moveState}
                    </span>
                  </div>
                  {lastLocation && (
                    <div className="mt-1">
                      <AddressDisplay
                        lat={lastLocation.latitude}
                        lon={lastLocation.longitude}
                        className="text-xs text-gray-500"
                        compact
                      />
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-sm text-gray-400">No data</span>
              )}
            </div>

            {/* Alerts (merged SQL + ML anomalies + spoofs) */}
            <div className="p-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                <AlertTriangle size={13} />
                Alerts
              </div>
              {mergedAlertCount > 0 ? (
                <div>
                  <span className="text-2xl font-bold text-amber-600">{mergedAlertCount}</span>
                  <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 mt-1">
                    {highAlerts.length > 0 && <span>{highAlerts.length} anomaly</span>}
                    {flaggedSpoofs.length > 0 && <span>{flaggedSpoofs.length} spoof</span>}
                    {(risk.riskAlertCount || 0) > 0 && <span>{risk.riskAlertCount} risk</span>}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <CheckCircle size={18} className="text-green-400" />
                  <span className="text-sm text-green-600 font-medium">None</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Row 2: Two-column detail cards ── */}
      {(caseSummary || hasMlData) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Left: Compliance & Court */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <ClipboardCheck size={18} className="text-blue-500" />
              Compliance & Court
            </h3>
            <div className="space-y-3 text-sm">
              {/* Check-ins */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 font-medium">Check-ins</span>
                  <span className="text-gray-700">
                    <span className="font-semibold text-green-600">{checkIns.completedCheckIns || 0}</span>
                    <span className="text-gray-400 mx-0.5">/</span>
                    <span className="font-semibold">{checkIns.totalCheckIns || 0}</span>
                    {(checkIns.missedCheckIns || 0) > 0 && (
                      <span className="text-red-600 font-semibold ml-1.5">({checkIns.missedCheckIns} missed)</span>
                    )}
                  </span>
                </div>
                <MiniProgressBar
                  value={checkIns.completedCheckIns || 0}
                  max={checkIns.totalCheckIns || 1}
                  color={isComplianceGood ? 'bg-green-500' : 'bg-red-500'}
                />
              </div>

              {/* Court events */}
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-medium">Court appearances</span>
                <span className="text-gray-700">
                  <span className="font-semibold text-green-600">{courtEvents.appearedCount || 0}</span>
                  <span className="text-gray-400 mx-0.5">/</span>
                  <span className="font-semibold">{courtEvents.totalCourtEvents || 0}</span>
                  {(courtEvents.noShowCount || 0) > 0 && (
                    <span className="text-red-600 font-semibold ml-1.5">({courtEvents.noShowCount} no-show)</span>
                  )}
                </span>
              </div>

              {/* Geofence violations — only shown when geofencing is enabled for this org */}
              {locationData.geofenceEnabled && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-medium">Geofence violations</span>
                <span className={`font-semibold ${(locationData.geofenceViolationCount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {locationData.geofenceViolationCount || 0}
                </span>
              </div>
              )}

              {/* Curfew violations (only if any) */}
              {(locationData.curfewViolationCount || 0) > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Curfew violations</span>
                  <span className="font-semibold text-red-600">{locationData.curfewViolationCount}</span>
                </div>
              )}

              {/* Bond & Payment */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium flex items-center gap-1">
                    <DollarSign size={14} className="text-emerald-500" />
                    Bond
                  </span>
                  <span className="font-semibold text-gray-700">{cs.bondType || 'N/A'} &mdash; {cs.bondAmountFormatted || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Payment rate</span>
                  <span className="font-semibold text-gray-700">{cs.paymentCompletionRateFormatted || '100%'}</span>
                </div>
                {(payments.outstanding || 0) > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Outstanding</span>
                    <span className="font-semibold text-red-600">${payments.outstanding.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Analytics & Data Quality */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <BarChart3 size={18} className="text-indigo-500" />
              Analytics & Data Quality
            </h3>
            {hasMlData ? (
              <div className="space-y-3 text-sm">
                {/* Risk Score with bar */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-500 font-medium">Risk Score</span>
                    {riskValue !== null ? (
                      <span className="flex items-center gap-1.5">
                        <span className={`text-lg font-bold ${riskConfig?.text}`}>{riskValue.toFixed(2)}</span>
                        <span className="text-gray-400 text-xs">/ 5.00</span>
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs italic">Pending</span>
                    )}
                  </div>
                  {riskValue !== null && (
                    <MiniProgressBar
                      value={riskValue - 1}
                      max={4}
                      color={riskValue >= 4 ? 'bg-red-500' : riskValue >= 3 ? 'bg-orange-500' : riskValue >= 2 ? 'bg-yellow-500' : 'bg-green-500'}
                    />
                  )}
                </div>

                {/* Behavior profile */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Behavior profile</span>
                  {clusterLabel ? (
                    <span className="flex items-center gap-1.5">
                      <span>{clusterConfig?.icon || ''}</span>
                      <span className="font-semibold" style={{ color: clusterConfig?.color }}>
                        {clusterConfig?.label || clusterLabel}
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs italic">Pending</span>
                  )}
                </div>

                {/* GPS Data Trust with bar */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-500 font-medium flex items-center gap-1">
                      <Wifi size={14} className={qualityConfig?.text || 'text-gray-400'} />
                      GPS Data Trust
                    </span>
                    {avgQuality != null ? (
                      <span className={`font-bold ${qualityConfig?.text}`}>
                        {Math.round(avgQuality)}% <span className="font-normal text-xs">{qualityConfig?.label}</span>
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs italic">Pending</span>
                    )}
                  </div>
                  {avgQuality != null && (
                    <MiniProgressBar
                      value={avgQuality}
                      max={100}
                      color={avgQuality >= 80 ? 'bg-green-500' : avgQuality >= 50 ? 'bg-yellow-500' : 'bg-red-500'}
                    />
                  )}
                </div>

                {/* Geofence compliance (ML live check) */}
                {mlIsCompliant != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-medium">Geofence (live)</span>
                    <span className={`flex items-center gap-1 font-semibold ${mlIsCompliant ? 'text-green-600' : 'text-red-600'}`}>
                      {mlIsCompliant ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {mlIsCompliant ? 'Compliant' : 'Non-Compliant'}
                    </span>
                  </div>
                )}

                {/* Pattern stability link */}
                <div className="border-t border-gray-100 pt-2">
                  <button
                    onClick={() => setActiveTab?.('location')}
                    className="text-xs text-blue-500 hover:text-blue-700 hover:underline"
                  >
                    View pattern stability on Location tab &rarr;
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <BarChart3 size={28} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No ML analytics available yet.</p>
                <p className="text-gray-400 text-xs mt-1">
                  Run analysis on the{' '}
                  <button onClick={() => setActiveTab?.('location')} className="text-blue-500 hover:underline">Location</button> or{' '}
                  <button onClick={() => setActiveTab?.('risk')} className="text-blue-500 hover:underline">Risk</button> tab.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolOverviewTab;
